import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Pencil, Copy, Trash2, FileText, ClipboardCheck } from 'lucide-react';
import { opportunitiesAPI } from '../lib/api';
import { BACKEND_URL as API } from '../lib/config';
import { Switch } from './ui/switch';
import { PageHero } from './ModernPage';
import { useSettings } from '../App';
import { opportunitySkin } from '../lib/opportunitySkin';

// ─────────────────────────────────────────────────────────────────────────
// «Moderno» /opportunities/develop (s141) — approved by Anthony: "Ledger".
// Graphite page header, underline tabs (My Opportunities / Review Queue),
// "Show archived" switch, one row per opportunity (photo, name + type, goal,
// launch, status, actions). Same API calls and rules as the default page
// (edit, clone, delete only for draft/archived, review queue → review page).
// Rendered only when settings.active_theme === 'modern'.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "var(--op-head, 'Playfair Display', Georgia, serif)";
const GRAPHITE = 'var(--op-dark, #16171a)';
const shell = 'max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)]';
const line = 'rgba(20,26,58,.1)';
const faint = 'rgba(20,26,58,.5)';
const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);
const money = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const day = (iso) => {
  if (!iso) return '—';
  const d = new Date(String(iso).length === 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
const STATUS = {
  draft: { label: 'Draft', style: { background: 'rgba(20,26,58,.07)', color: 'var(--color-heading, #1a2332)' } },
  pending_review: { label: 'Pending review', style: { background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 18%, #fff)', color: 'var(--op-strong, #b86500)' } },
  published: { label: 'Published', style: { background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #16171a)' } },
  archived: { label: 'Archived', style: { background: 'rgba(20,26,58,.05)', color: faint } },
};
const COLS = 'md:grid-cols-[84px_minmax(0,1fr)_130px_130px_130px_112px]';

export function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.draft;
  return <span className="inline-block text-[11px] font-bold px-3 py-1 rounded-full whitespace-nowrap" style={s.style}>{s.label}</span>;
}

function Thumb({ o }) {
  const src = (o.images || []).find(Boolean);
  return (
    <span className="block aspect-[4/3] w-[72px] md:w-auto rounded-[var(--op-radius,3px)] overflow-hidden" style={{ background: GRAPHITE }}>
      {src && <img src={img(src)} alt="" loading="lazy" className="w-full h-full object-cover" />}
    </span>
  );
}

function IconBtn({ title, onClick, danger, children, ...rest }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick}
      className={`w-9 h-9 grid place-items-center rounded-full transition-colors duration-150 hover:bg-[rgba(20,26,58,.06)] ${danger ? 'hover:text-red-600' : 'hover:text-[color:var(--color-heading,#1a2332)]'}`}
      style={{ color: faint }} {...rest}>{children}</button>
  );
}

function Head({ children }) {
  return <div className={`hidden md:grid ${COLS} gap-5 pt-5 pb-2.5 text-[11px] font-semibold uppercase`} style={{ letterSpacing: '.08em', color: faint }}>{children}</div>;
}

function Empty({ icon: Icon, title, hint }) {
  return (
    <div className="py-16 text-center border-t" style={{ borderColor: line }}>
      <Icon className="w-9 h-9 mx-auto mb-3" style={{ color: 'rgba(20,26,58,.2)' }} />
      <p className="font-semibold" style={{ color: 'var(--color-heading, #1a2332)' }}>{title}</p>
      {hint && <p className="text-sm mt-1" style={{ color: faint }}>{hint}</p>}
    </div>
  );
}

function Skeleton() {
  return (
    <ul>{Array.from({ length: 4 }).map((_, i) => (
      <li key={i} className="flex items-center gap-5 py-3.5 border-t" style={{ borderColor: line }}>
        <span className="w-[84px] aspect-[4/3] rounded-[var(--op-radius,3px)] animate-pulse" style={{ background: 'rgba(20,26,58,.06)' }} />
        <span className="h-4 flex-1 max-w-md rounded animate-pulse" style={{ background: 'rgba(20,26,58,.06)' }} />
      </li>
    ))}</ul>
  );
}

function ReviewQueue({ typeNames }) {
  const navigate = useNavigate();
  const [items, setItems] = React.useState(null);
  React.useEffect(() => { opportunitiesAPI.reviewQueue().then(r => setItems(r.data || [])).catch(() => setItems([])); }, []);
  if (items === null) return <Skeleton />;
  if (!items.length) return <Empty icon={ClipboardCheck} title="Nothing waiting for your review." />;
  return (
    <>
      <Head><span /><span>Opportunity</span><span>Goal</span><span>Author</span><span>Your review</span><span /></Head>
      <ul>
        {items.map(o => (
          <li key={o.id} className={`grid grid-cols-[72px_minmax(0,1fr)_auto] ${COLS} gap-x-5 gap-y-2 items-center py-3.5 border-t`} style={{ borderColor: line }} data-testid={`review-row-${o.id}`}>
            <Thumb o={o} />
            <div className="min-w-0 flex flex-col gap-0.5">
              <span className="font-semibold" style={{ fontFamily: SERIF, fontSize: '1.05rem', lineHeight: 1.3, color: 'var(--color-heading, #1a2332)' }}>{o.name}</span>
              <span className="text-[13px]" style={{ color: faint }}>{typeNames[o.type_id] || '—'}</span>
            </div>
            <span className="hidden md:block text-sm font-bold tabular-nums" style={{ color: 'var(--color-heading, #1a2332)' }}>{money(o.total_amount)}</span>
            <span className="hidden md:block text-sm truncate" style={{ color: faint }}>{o.author || '—'}</span>
            <span className="hidden md:block">
              {o.my_review
                ? <span className="inline-block text-[11px] font-bold px-3 py-1 rounded-full" style={o.my_review.flag === 'approve' ? STATUS.published.style : { background: 'rgba(180,35,24,.08)', color: '#b42318' }}>{o.my_review.flag === 'approve' ? 'Approved' : 'Rejected'}</span>
                : <span style={{ color: faint }}>—</span>}
              {o.threshold != null && <span className="block text-[11px] mt-1 tabular-nums" style={{ color: faint }}>{o.approvals}/{o.threshold} approvals</span>}
            </span>
            <span className="justify-self-end">
              <button type="button" onClick={() => navigate(`/opportunities/develop/review/${o.id}`)}
                className="mx-btn mx-btn--secondary px-4 py-1.5 rounded-full border text-[13px] font-bold" style={{ borderColor: 'rgba(20,26,58,.2)', color: 'var(--color-heading, #1a2332)' }}
                data-testid={`review-btn-${o.id}`}>Review</button>
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

export default function ModernOpportunitiesDevelopment() {
  const skin = opportunitySkin(useSettings().active_theme); // s153: all brands, per-theme skin
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'review' ? 'review' : 'mine';
  const [items, setItems] = React.useState(null);
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [typeNames, setTypeNames] = React.useState({});
  const [canReview, setCanReview] = React.useState(false); // Opportunity Reviewer role or admin
  React.useEffect(() => { opportunitiesAPI.reviewAccess().then(r => setCanReview(!!r.data.can_review)).catch(() => setCanReview(false)); }, []);

  const load = React.useCallback(() => {
    opportunitiesAPI.mine(includeArchived)
      .then(r => setItems(r.data || []))
      .catch(() => { setItems([]); toast.error('Failed to load your opportunities'); });
  }, [includeArchived]);
  React.useEffect(load, [load]);
  React.useEffect(() => {
    opportunitiesAPI.types().then(r => setTypeNames(Object.fromEntries((r.data || []).map(t => [t.type_id, t.name])))).catch(() => {});
  }, []);

  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.name}"? It will disappear from your list.`)) return;
    try { await opportunitiesAPI.remove(o.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  const clone = async (o) => {
    try {
      const r = await opportunitiesAPI.clone(o.id);
      toast.success('Cloned — you are now editing the copy');
      navigate(`/opportunities/develop/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Clone failed'); }
  };

  const tabBtn = (k, label, count) => (
    <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setSearchParams(k === 'review' ? { tab: 'review' } : {})}
      className="py-3 -mb-px text-sm font-semibold border-b-2 transition-colors duration-150"
      style={{ color: tab === k ? 'var(--color-heading, #1a2332)' : faint, borderColor: tab === k ? 'var(--color-accent, #ff9c1b)' : 'transparent' }}
      data-testid={`tab-${k}`}>
      {label}{count != null && <span className="ml-1.5 tabular-nums" style={{ opacity: 0.55 }}>{count}</span>}
    </button>
  );

  return (
    <div className="bg-white" data-testid="opportunities-develop" style={skin}>
      <PageHero title="My Opportunities" summary="Draft, refine and submit your investment opportunities. Published entries appear in the member directory."
        trail={[{ label: 'Home', to: '/' }, { label: 'Opportunities', to: '/opportunities' }, { label: 'Develop' }]}
        badge={(
          <Link to="/opportunities/develop/new" className="mx-btn mx-btn--primary order-last self-start inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold"
            style={{ background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #16171a)' }} data-testid="add-opportunity-btn">
            <Plus className="w-4 h-4" /> New Opportunity
          </Link>
        )} />
      <div className={`${shell} pt-8 pb-16`}>
        <div className="flex flex-wrap items-center justify-between gap-4 border-b" style={{ borderColor: line }}>
          <div className="flex gap-7" role="tablist" aria-label="Opportunities">
            {tabBtn('mine', 'My Opportunities', items ? items.length : null)}
            {canReview && tabBtn('review', 'Review Queue')}
          </div>
          {tab === 'mine' && (
            <label className="inline-flex items-center gap-2.5 text-[13px] font-semibold cursor-pointer" style={{ color: 'var(--color-heading, #1a2332)' }}>
              <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} className="data-[state=checked]:bg-[color:var(--color-heading,#1a2332)]" data-testid="include-archived-toggle" />
              Show archived
            </label>
          )}
        </div>

        {tab === 'review' && canReview ? <ReviewQueue typeNames={typeNames} /> : items === null ? <Skeleton /> : !items.length ? (
          <Empty icon={FileText} title="No opportunities yet." hint="Create your first one. It stays a private draft until you submit it." />
        ) : (
          <>
            <Head><span /><span>Opportunity</span><span>Goal</span><span>Launch</span><span>Status</span><span /></Head>
            <ul>
              {items.map(o => {
                const editable = o.status === 'draft' || o.status === 'archived';
                return (
                  <li key={o.id} className={`grid grid-cols-[72px_minmax(0,1fr)_auto] ${COLS} gap-x-5 gap-y-2 items-center py-3.5 border-t`} style={{ borderColor: line }} data-testid={`opportunity-row-${o.id}`}>
                    <Link to={`/opportunities/develop/${o.id}`} tabIndex={-1} aria-hidden="true"><Thumb o={o} /></Link>
                    <div className="min-w-0 flex flex-col gap-0.5">
                      <Link to={`/opportunities/develop/${o.id}`} className="mx-link font-semibold self-start" style={{ fontFamily: SERIF, fontSize: '1.05rem', lineHeight: 1.3, color: 'var(--color-heading, #1a2332)' }}>{o.name || '(untitled)'}</Link>
                      <span className="text-[13px]" style={{ color: faint }}>{typeNames[o.type_id] || '—'}<span className="md:hidden"> &middot; {money(o.total_amount)}</span></span>
                      <span className="md:hidden mt-1"><StatusChip status={o.status} /></span>
                    </div>
                    <span className="hidden md:block text-sm font-bold tabular-nums" style={{ color: 'var(--color-heading, #1a2332)' }}>{money(o.total_amount)}</span>
                    <span className="hidden md:block text-sm font-semibold tabular-nums" style={{ color: faint }}>{day(o.dates?.launch)}</span>
                    <span className="hidden md:block"><StatusChip status={o.status} /></span>
                    <span className="justify-self-end flex">
                      <IconBtn title="Edit" onClick={() => navigate(`/opportunities/develop/${o.id}`)}><Pencil className="w-4 h-4" /></IconBtn>
                      <IconBtn title="Clone" onClick={() => clone(o)}><Copy className="w-4 h-4" /></IconBtn>
                      {editable && <IconBtn title="Delete" danger onClick={() => remove(o)}><Trash2 className="w-4 h-4" /></IconBtn>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
